import Logger from 'js-logger'
import {
    clearAuthTokens,
    getAccessToken,
    setAuthTokens,
    useAuthTokenInterceptor,
    refreshTokenIfNeeded,
} from './jwt'
import api, { axios, getPlainAxios } from './api'
import { CreateObtainAuthTokenRequest } from '../types/swagger'
import { ignoreError } from './errorTracker'

// Swagger docs are incorrect so type manually
export interface Jwt {
    readonly refresh: string
    readonly access: string
    readonly token: string
}

// Swagger docs are incorrect so type manually
export interface LoginInput {
    username: string
    password: string
}

const storeTokens = (jwt: Jwt) => setAuthTokens({
    accessToken: jwt.access,
    refreshToken: jwt.refresh,
})

const requestRefresh = async (refresh: string): Promise<string> => {
    // This request should not be send with our axios client that has
    // the token interceptor enabled.
    const jwt = await getPlainAxios().post<Jwt>('/auth/jwt/refresh/', { refresh })
    return jwt.data.access
}

// eslint-disable-next-line react-hooks/rules-of-hooks
useAuthTokenInterceptor(axios, { requestRefresh })

const loginUsername = async (params: LoginInput) => {
    const response = await api.safePost<Jwt>('/auth/jwt/login/', {
        ...params,
        username: params.username.trim(),
    })
    if (response.error) { return response }
    Logger.debug('JWT is set')
    storeTokens(response.data)
    return response
}

const enterEvent = async (params: CreateObtainAuthTokenRequest) => {
    const response = await api.safePost<Jwt>('/auth/obtain/', {
        ...params,
        email: params.email?.trim(),
    })
    if (response.error) { return response }
    storeTokens(response.data)
    return response
}

const collectErrorMessages = (error: unknown): string[] => {
    const messages: string[] = []

    if (typeof error === 'string') {
        return [error]
    }

    if (Array.isArray(error)) {
        error.forEach((item) => {
            if (typeof item === 'string') {
                messages.push(item)
            }
        })
        return messages
    }

    if (!error || typeof error !== 'object') {
        return messages
    }

    const errorObject = error as Record<string, unknown>

    if (typeof errorObject.message === 'string') {
        messages.push(errorObject.message)
    }

    const nestedErrors = errorObject.errors
    if (typeof nestedErrors === 'string') {
        messages.push(nestedErrors)
    } else if (Array.isArray(nestedErrors)) {
        nestedErrors.forEach((item) => {
            if (typeof item === 'string') {
                messages.push(item)
            }
        })
    } else if (nestedErrors && typeof nestedErrors === 'object') {
        Object.values(nestedErrors as Record<string, unknown>).forEach((value) => {
            if (typeof value === 'string') {
                messages.push(value)
            } else if (Array.isArray(value)) {
                value.forEach((item) => {
                    if (typeof item === 'string') {
                        messages.push(item)
                    }
                })
            }
        })
    }

    if (!messages.length) {
        Object.values(errorObject).forEach((value) => {
            if (typeof value === 'string') {
                messages.push(value)
            } else if (Array.isArray(value)) {
                value.forEach((item) => {
                    if (typeof item === 'string') {
                        messages.push(item)
                    }
                })
            }
        })
    }

    return Array.from(new Set(messages.filter(Boolean)))
}

const getApiErrorMessage = (error: unknown, fallback: string): string => {
    const messages = collectErrorMessages(error)
    return messages.length ? messages.join(' ') : fallback
}

type LoginInputUnion = LoginInput | CreateObtainAuthTokenRequest;

function isLoginInput(input: LoginInputUnion): input is LoginInput {
    return (input as LoginInput).password !== undefined
}

const resetPassword = async (email: string) => {
    const response = await api.safePost('/auth/reset-password/', { email: email.trim() })

    if (response.error) {
        throw new Error(getApiErrorMessage(response.error, 'Failed to send reset email. Please try again.'))
    }

    return response.data
}

const confirmResetPassword = async (uid: string, token: string, newPassword: string) => {
    const response = await api.safePost('/auth/confirm-password-reset/', {
        uid,
        token,
        new_password: newPassword,
    })

    if (response.error) {
        throw new Error(
            getApiErrorMessage(
                response.error,
                'Failed to reset password. The link may be invalid or expired.',
            ),
        )
    }

    return response.data
}

export default {
    getAccessToken,
    storeTokens,
    refreshToken: () => ignoreError(() => refreshTokenIfNeeded(requestRefresh)),
    login: async (input: LoginInputUnion) => {
        if (isLoginInput(input)) {
            return loginUsername(input)
        }
        return enterEvent(input)
    },
    logout: clearAuthTokens,
    resetPassword,
    confirmResetPassword,
}
